import { Textbox } from "fabric";
import React, { useEffect, useState } from "react";
import BackgroundModal from "./BackgroundModal";
import LayoutSelectorModal from "./LayoutSelectorModal";
import SignatureModal from "./SignatureModal";
import TextPropertiesSelectorModal from "./TextPropertiesSelectorModal";
import ZoomEditorModal from "./ZoomEditorModal";
import { ModalType } from "../../Context/ModalContext";
import TextGeneratorModal from "./TextGeneratorModal";
import AITextGeneratorModal from "./AITextGeneratorModal";
import * as fabric from "fabric";

interface TextPart {
  text: string;
  isHighlighted: boolean;
}

const MODAL_COMPONENTS = {
  [ModalType.font]: TextPropertiesSelectorModal,
  [ModalType.textColor]: TextPropertiesSelectorModal,
  [ModalType.textSize]: TextPropertiesSelectorModal,
  [ModalType.zoom]: ZoomEditorModal,
  [ModalType.background]: BackgroundModal,
  [ModalType.layout]: LayoutSelectorModal,
  [ModalType.signature]: SignatureModal,
  [ModalType.textGenerator]: TextGeneratorModal,
  [ModalType.aItextGenerator]: AITextGeneratorModal,
};

/**
 * Decodes HTML entities in a given text string.
 *
 * This function uses the DOMParser to parse the input text as HTML and then extracts
 * the text content, effectively decoding any HTML entities present in the input string.
 *
 * @param text - The input string containing HTML entities to be decoded.
 * @returns The decoded string with HTML entities converted to their corresponding characters.
 */
export function decodeHtmlEntities(text) {
  const parser = new DOMParser();
  const decodedString = parser.parseFromString(text, "text/html").documentElement.textContent;
  return decodedString;
}

/**
 * Renders a modal component based on the provided modal type.
 *
 * @param {ModalType} type - The type of modal to render. Determines the modal component to display.
 * @param {fabric.Canvas} fabricCanvasInstanceRef - Reference to the current fabric canvas instance, used to access canvas
 *   objects and pass context to modals.
 * @param {number} [index] - Optional index of the targeted object on the canvas. Used to retrieve specific object data
 *   for certain modals.
 *
 * @returns {React.Component|null} The modal component corresponding to the provided type, or `null` if no matching
 *   modal type is found.
 */
export const renderModal = (type: ModalType, fabricCanvasInstanceRef: fabric.Canvas, index?: number ): React.JSX.Element | null => {
  const ModalComponent = MODAL_COMPONENTS[type];

  if (!ModalComponent) return null;

  const data = index !== undefined && fabricCanvasInstanceRef._objects[index]
               ? fabricCanvasInstanceRef._objects[index]
               : null;

  switch (type) {
    case ModalType.font:
    case ModalType.textColor:
      return (
        <ModalComponent
          key={`modal-${type}-${(data as Textbox)?.fontFamily || 'default'}`}
          canvas={fabricCanvasInstanceRef}
        />
      );
    default:
      return <ModalComponent canvas={fabricCanvasInstanceRef} />;
  }
};

/**
 * A React functional component that simulates a typing effect while rendering specific portions
 * of the text with highlighted styles. The component dynamically displays characters and applies
 * custom styling to text enclosed in square brackets.
 *
 * @param {Object} props - The properties for the TypingEffectWithHighlights component.
 * @param {string} props.text - The full text string to be displayed with typing effect and conditional highlighting.
 *    Text enclosed within square brackets (`[...]`) will be highlighted.
 *    Supports multiline string with `\n` for line breaks.
 * @param {number} [props.typingSpeed=50] - The speed of the typing effect in milliseconds per character.
 *
 * @returns {JSX.Element} A paragraph element displaying the progressive typing effect, with styled highlighted fragments.
 */
export const TypingEffectWithHighlights = ({ text, typingSpeed = 50 }) => {
  const [displayedText, setDisplayedText] = useState(""); // Progressive text
  const [currentIndex, setCurrentIndex] = useState(0); // Current character index

  // Splits the text into fragments with styling based on brackets
  const parseText = (text: string) => {
    const regex = /(\[.*?\])/g; // Includes brackets in the match
    const parts: TextPart[] = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the brackets
      if (match.index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, match.index), isHighlighted: false });
      }
      // Add text with brackets
      parts.push({ text: match[0], isHighlighted: true });
      lastIndex = regex.lastIndex;
    }

    // Add text after the last bracket
    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), isHighlighted: false });
    }

    return parts;
  };

  const parsedText = parseText(text);

  useEffect(() => {
    let timer;
    if (currentIndex < text.length) {
      timer = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1)); // Adds one character at a time
        setCurrentIndex((prev) => prev + 1);
      }, typingSpeed);
    }
    return () => clearTimeout(timer); // Timer cleanup
  }, [currentIndex, text, typingSpeed]);

  // Generates styled fragments for the already displayed text
  const renderHighlightedText = () => {
    const fragments: (JSX.Element | string)[] = [];
    let displayedLength = 0;

    for (const part of parsedText) {
      if (displayedLength >= displayedText.length) break;

      const visibleText = part.text.slice(0, displayedText.length - displayedLength);
      displayedLength += visibleText.length;

      if (part.isHighlighted) {
        fragments.push(
          <span
            key={displayedLength}
            style={{ color: "#CB62B4" }}
          >
            {visibleText}
          </span>
        );
      } else {
        // Handle line breaks in non-styled text
        const splitLines = visibleText.split("\n").map((line, index) => (
          <React.Fragment key={`${displayedLength}-${index}`}>
            {index > 0 && <br />} {/* Adds a <br /> between lines */}
            {line}
          </React.Fragment>
        ));
        fragments.push(...splitLines);
      }
    }

    return fragments;
  };

  return <p className="text-secondary">{renderHighlightedText()}</p>;
};
